import { useRef, useCallback, useEffect } from 'preact/hooks';
import { useReorderableItem } from '../hooks/PRIVATE_useReorderable';
import { useUser } from '../hooks/UNSAFE_useUser';
import { classNames } from '../utils/UNSAFE_classNames';
import { ReorderableItemProps } from '../UNSAFE_Collection/Reorderable.types';
import { multiVariantStyles, styles } from './themes/BaseCardViewStyles.css';
import { CARDVIEW_ITEM_SELECTOR, GutterSize } from './BaseCardViewUtils';
import { useReorderableContext } from '#hooks/PRIVATE_useReorderable/useReorderableContext';
import { isKeyDefined } from '../utils/UNSAFE_keys';

type BaseItemProps<K> = {
  itemKey: K;
  itemIndex: number;
  isCurrentKey: boolean;
};

export type BaseCardViewReorderableItemProps<K> = ReorderableItemProps<K> & {
  gutterSize: GutterSize;
  columns: number;
};

/**
 * The internal component used to render a reorderable item in CardView.
 */
export function BaseCardViewReorderableItem<K extends string | number>({
  children,
  itemKey,
  itemIndex,
  isCurrentKey,
  dragKey,
  setDragKey,
  onReorder,
  rootRef,
  gutterSize,
  columns
}: BaseCardViewReorderableItemProps<K> & BaseItemProps<K>) {
  const itemRef = useRef<HTMLDivElement | null>(null);
  const isOneColumn = columns === 1;
  const isFirstColumn = itemIndex % columns === 0;
  const isLastColumn = itemIndex % columns === columns - 1;
  const variantClasses = multiVariantStyles({
    itemOverlayFront: gutterSize
  });
  const variantClassesVertical = multiVariantStyles({
    itemOverlayFrontVertical: gutterSize
  });
  const itemStyles = {
    itemOverlayFront: isOneColumn ? variantClassesVertical : variantClasses,
    itemOverlayRear: isOneColumn ? styles.itemOverlayRearVertical : styles.itemOverlayRear,
    itemDragger: styles.itemDragger
  };
  const getItem = useCallback(
    () => itemRef?.current?.closest(CARDVIEW_ITEM_SELECTOR) as HTMLElement,
    []
  );
  const setTransferData = useCallback(
    (event: DragEvent) => {
      event.dataTransfer?.setData('text/cardview-items-key', JSON.stringify(itemKey));
    },
    [itemKey]
  );
  const { rootProps, isDragged, overlayZoneBeforeProps, overlayZoneAfterProps } =
    useReorderableItem({
      isDisabled: onReorder ? false : true,
      dragKey,
      setDragKey,
      onReorder,
      rootRef,
      ref: itemRef,
      getItem,
      setTransferData,
      itemStyles,
      itemSelector: CARDVIEW_ITEM_SELECTOR,
      isVertical: isOneColumn
    });

  // TODO: remove this as part of JET-70023
  const { currentItemKey } = useReorderableContext();
  useEffect(() => {
    if (isCurrentKey) {
      currentItemKey.current = itemKey;
    }
  }, [isCurrentKey, itemKey, currentItemKey]);

  const { direction } = useUser();
  const isRtl = direction === 'rtl';

  const overlayZoneBeforeClass = classNames([
    overlayZoneBeforeProps?.class,
    !isOneColumn && !isRtl && isFirstColumn && styles.itemOverlayFrontEdge,
    !isOneColumn && isRtl && isLastColumn && styles.itemOverlayFrontEdge
  ]);

  const overlayZoneAfterClass = classNames([
    overlayZoneAfterProps?.class,
    !isOneColumn && !isRtl && isLastColumn && styles.itemOverlayRearEdge,
    !isOneColumn && isRtl && isFirstColumn && styles.itemOverlayRearEdge
  ]);

  return (
    <div {...rootProps}>
      {children}
      {!isDragged && isKeyDefined(dragKey) && (
        <>
          <div {...overlayZoneBeforeProps} class={overlayZoneBeforeClass}></div>
          <div {...overlayZoneAfterProps} class={overlayZoneAfterClass}></div>
        </>
      )}
    </div>
  );
}
